import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ClassroomFrameComponent } from './classroom-frame.component';
import { LessonspaceDoorService } from './lessonspace-door.service';
import { RoomLink } from './room-link';

interface SavedJoin {
  userId: string;
  name: string;
}

export function savedJoinKey(roomId: string): string {
  return `future-room.join.${roomId}`;
}

function readSavedJoin(roomId: string): SavedJoin | null {
  const raw = sessionStorage.getItem(savedJoinKey(roomId));
  if (!raw) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed &&
      'userId' in parsed &&
      'name' in parsed &&
      typeof parsed.userId === 'string' &&
      typeof parsed.name === 'string' &&
      parsed.userId &&
      parsed.name
    ) {
      return { userId: parsed.userId, name: parsed.name };
    }
  } catch {
    return null;
  }
  return null;
}

@Component({
  selector: 'app-student-door',
  imports: [TranslocoPipe, ClassroomFrameComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './student-door.component.html',
  styles: `
    :host {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      min-height: 0;
      min-width: 0;
    }
  `,
})
export class StudentDoorComponent implements OnInit {
  private readonly door = inject(LessonspaceDoorService);
  private readonly route = inject(ActivatedRoute);
  private readonly transloco = inject(TranslocoService);
  private readonly roomLink = inject(RoomLink);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly roomId = signal<string | null>(null);
  protected readonly name = signal('');
  protected readonly joining = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly classroomUrl = signal<string | null>(null);

  ngOnInit(): void {
    const roomId = this.route.snapshot.paramMap.get('roomId')?.trim() ?? '';
    if (!roomId) {
      return;
    }
    this.roomId.set(roomId);
    this.roomLink.set(roomId);
    const saved = readSavedJoin(roomId);
    if (!saved) {
      return;
    }
    this.name.set(saved.name);
    this.joinRoom(saved.userId);
  }

  protected updateNameFromInput(event: Event): void {
    const target = event.target;
    if (target instanceof HTMLInputElement) {
      this.name.set(target.value);
    }
  }

  protected submitJoin(event: Event): void {
    event.preventDefault();
    const roomId = this.roomId();
    const name = this.name().trim();
    if (!roomId || !name || this.joining()) {
      return;
    }
    const saved = readSavedJoin(roomId);
    const userId = saved?.name === name ? saved.userId : crypto.randomUUID();
    this.joinRoom(userId);
  }

  private roomDark(): boolean {
    return document.documentElement.classList.contains('pl-dark');
  }

  private joinRoom(userId: string): void {
    const roomId = this.roomId();
    const name = this.name().trim();
    if (!roomId || !name) {
      return;
    }

    this.joining.set(true);
    this.error.set(null);
    this.door
      .joinSpace(roomId, {
        name,
        userId,
        locale: this.transloco.getActiveLang(),
        darkmode: this.roomDark(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: joined => {
          sessionStorage.setItem(
            savedJoinKey(roomId),
            JSON.stringify({ userId: joined.userId, name })
          );
          this.classroomUrl.set(joined.clientUrl);
          this.joining.set(false);
        },
        error: () => {
          this.joining.set(false);
          this.error.set('door.joinFailed');
        },
      });
  }
}
